import React, { useContext } from "react";
import MenuItem from "@mui/material/MenuItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import { ListItemText, PopoverProps, SvgIconProps } from "@mui/material";
import Menu from "@mui/material/Menu";
import {
  IMessage,
  MESSAGE_CONTENT_TYPE,
  MESSAGE_CONTEXT_MENU,
  StarException,
  TemplateKey,
  UnStarException,
} from "@home-health-notify/domain";
import {
  ApiErrorHandler,
  formMessageToText,
  ForwardMessageContext,
  IChatInfoAccess,
  MessagePanelContext,
  RecipientInfoContext,
  ReplyMessageContext,
  useTranslation,
} from "hhn-commons";
import InfoIcon from "@mui/icons-material/Info";
import ThumbUpOffAltIcon from "@mui/icons-material/ThumbUpOffAlt";
import StarBorderPurple500Icon from "@mui/icons-material/StarBorderPurple500";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import ReplyIcon from "@mui/icons-material/Reply";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import RepeatIcon from "@mui/icons-material/Repeat";
import RepeatOneIcon from "@mui/icons-material/RepeatOne";
import EditIcon from "@mui/icons-material/Edit";
import { ForwardedIcon } from "../MessagePanel.style";
import { useSnackbar } from "notistack";

export interface MessageContextMenuAction {
  key: MESSAGE_CONTEXT_MENU;
  id: string;
  disabled: boolean;
  title: string;
  icon: (props: SvgIconProps) => React.ReactNode;
  handler: (message: IMessage) => Promise<void>;
  sx?: object;
}

export interface BubbleMenuItemsType {
  title: string;
  key: MESSAGE_CONTEXT_MENU;
  icon: (props: SvgIconProps) => React.ReactNode;
  disabled: boolean;
  onClick?: () => void;
  id: string;
  sx?: object;
}

interface MessagePanelMenuType {
  message: IMessage;
  closeMenu: () => void;
  anchorEl: PopoverProps["anchorEl"];
  selectMessage: (message: IMessage) => void;
  onRequestDelete: (message: IMessage) => void;
  chatInfoAccess: IChatInfoAccess;
}

const MessagePanelMenu = ({
  closeMenu,
  anchorEl,
  message,
  selectMessage,
  onRequestDelete,
  chatInfoAccess,
}: MessagePanelMenuType) => {
  const { t } = useTranslation();
  const tNs = (key: string) => t(key, { ns: "message" });
  const open = Boolean(anchorEl);
  const { enqueueSnackbar } = useSnackbar();

  const { showMessageInfoModal, showAskAcknowledgementModal } =
    useContext(RecipientInfoContext);

  const { scrollToBottom, clearSelectedMessages, setMessageToEdit } =
    useContext(MessagePanelContext);
  const { setReplyMessage } = useContext(ReplyMessageContext);
  const { turnOnForwardMode } = useContext(ForwardMessageContext);

  const starMessage = async (message: IMessage) => {
    try {
      await message.starMessage();
    } catch (e) {
      if (e instanceof StarException) {
        enqueueSnackbar(tNs("message.starFailed"), {
          autoHideDuration: 3000,
        });
      } else {
        ApiErrorHandler(e, enqueueSnackbar);
      }
    }
  };

  const unStarMessage = async (message: IMessage) => {
    try {
      await message.removeStarredMessage();
    } catch (e) {
      if (e instanceof UnStarException) {
        enqueueSnackbar(tNs("message.unStarFailed"), {
          autoHideDuration: 3000,
        });
      } else {
        ApiErrorHandler(e, enqueueSnackbar);
      }
    }
  };

  const getCopyText = (message: IMessage) => {
    const messageData = message.getMessage();
    if (messageData.contentType === MESSAGE_CONTENT_TYPE.FORM) {
      return formMessageToText(
        messageData,
        chatInfoAccess?.getTemplateByKey?.(
          messageData.form?.templateType as TemplateKey,
        ),
      );
    }
    return messageData.body;
  };

  const copyMessage = async (message: IMessage) => {
    try {
      await navigator.clipboard.writeText(getCopyText(message));
      enqueueSnackbar(tNs("contextMenuMessages.messageCopied"), {
        autoHideDuration: 3000,
      });
    } catch (error) {
      enqueueSnackbar(tNs("contextMenuMessages.copyMessageError"), {
        autoHideDuration: 3000,
      });
    }
  };

  const contextMenuActions: MessageContextMenuAction[] = [
    {
      title: "contextMenu.info",
      key: MESSAGE_CONTEXT_MENU.INFO,
      disabled: false,
      handler: async (message: IMessage) => {
        showMessageInfoModal(message);
      },
      icon: InfoIcon,
      id: "info-menu-button",
    },
    {
      title: "contextMenu.askForAcknowledgement",
      key: MESSAGE_CONTEXT_MENU.ASK_ACKNOWLEDGEMENT,
      disabled: false,
      handler: async (message: IMessage) => {
        showAskAcknowledgementModal(message);
      },
      icon: ThumbUpOffAltIcon,
      id: "acknowledgement-menu-button",
    },
    {
      title: "contextMenu.starMessage",
      key: MESSAGE_CONTEXT_MENU.STAR_MESSAGE,
      disabled: false,
      handler: async (message: IMessage) => {
        await starMessage(message);
      },
      icon: StarBorderPurple500Icon,
      id: "star-menu-button",
    },
    {
      title: "contextMenu.unstarMessage",
      key: MESSAGE_CONTEXT_MENU.UNSTAR_MESSAGE,
      disabled: false,
      handler: async (message: IMessage) => {
        await unStarMessage(message);
      },
      icon: StarBorderPurple500Icon,
      id: "star-menu-button",
    },
    {
      title: "contextMenu.visibleToPatient",
      key: MESSAGE_CONTEXT_MENU.VISIBLE_TO_PATIENT,
      icon: VisibilityIcon,
      id: "visible-to-patient-menu-button",
      disabled: false,
      handler: async (message: IMessage) => {
        message.markMessageVisibleToPatient();
      },
    },
    {
      title: "contextMenu.visibleToPhysician",
      key: MESSAGE_CONTEXT_MENU.VISIBLE_TO_PHYSICIAN,
      icon: VisibilityIcon,
      id: "visible-to-physician-menu-button",
      disabled: false,
      handler: async (message: IMessage) => {
        message.markMessageVisibleToPhysician();
      },
    },
    {
      title: "contextMenu.copy",
      key: MESSAGE_CONTEXT_MENU.COPY,
      icon: ContentCopyIcon,
      id: "copy-menu-button",
      disabled: false,
      handler: async (message: IMessage) => {
        copyMessage(message);
      },
    },
    {
      title: "contextMenu.edit",
      key: MESSAGE_CONTEXT_MENU.EDIT,
      icon: EditIcon,
      id: "edit-menu-button",
      disabled: false,
      handler: async (message: IMessage) => {
        setMessageToEdit(message);
      },
    },
    {
      title: "contextMenu.resend",
      key: MESSAGE_CONTEXT_MENU.RESEND,
      icon: RepeatIcon,
      id: "resend-menu-button",
      disabled: false,
      handler: async (message: IMessage) => {
        message.resend();
        if (scrollToBottom) {
          scrollToBottom();
        }
      },
    },
    {
      title: "contextMenu.resendAsPriority",
      key: MESSAGE_CONTEXT_MENU.RESEND_AS_PRIORITY,
      icon: RepeatOneIcon,
      id: "resend-as-priority-menu-button",
      disabled: false,
      handler: async (message: IMessage) => {
        message.resendAsPriority();
        if (scrollToBottom) {
          scrollToBottom();
        }
      },
    },
    {
      title: "contextMenu.reply",
      key: MESSAGE_CONTEXT_MENU.REPLY,
      icon: ReplyIcon,
      id: "reply-menu-button",
      disabled: false,
      handler: async (message: IMessage) => {
        setReplyMessage(message);
      },
    },
    {
      title: "contextMenu.forwardMessage",
      key: MESSAGE_CONTEXT_MENU.FORWARD_MESSAGE,
      icon: (props: SvgIconProps) => <ForwardedIcon {...props} />,
      id: "forward-menu-button",
      disabled: false,
      handler: async (message: IMessage) => {
        clearSelectedMessages();
        selectMessage(message);
        turnOnForwardMode();
      },
    },
    {
      title: "contextMenu.deleteMessage",
      key: MESSAGE_CONTEXT_MENU.DELETE_MESSAGE,
      icon: DeleteForeverIcon,
      id: "delete-menu-button",
      disabled: false,
      handler: async (message: IMessage) => {
        onRequestDelete(message);
      },
    },
  ];

  return (
    <>
      <Menu
        id="basic-menu"
        data-testid="context-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={closeMenu}
        autoFocus={false}
        disableAutoFocusItem={false}
        variant="menu"
        slotProps={{
          list: {
            "aria-labelledby": "basic-button",
          },
        }}
      >
        {message.getContextMenu().map((action, i) => {
          const messageAction = contextMenuActions.find(a => a.key === action);
          if (!messageAction) {
            throw new Error(`Panic: Unsupported message menu action: action`);
          }

          const MenuIcon = messageAction.icon;
          return (
            <MenuItem
              data-testid={messageAction.id}
              onClick={() => {
                messageAction.handler(message);
                closeMenu();
              }}
              sx={{
                padding: "15px",
                ...(messageAction.sx || {}),
              }}
              key={tNs(messageAction.title) + "" + i}
              disabled={messageAction.disabled}
            >
              <ListItemIcon>
                <MenuIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>{tNs(messageAction.title)}</ListItemText>
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
};

export default MessagePanelMenu;
